import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Shield, Lock } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

const DEMO_ACCOUNTS = [
  { role: "Super Admin", email: "admin@cyberassess.com", password: "Admin@123" },
  { role: "Assessor", email: "assessor@cyberassess.com", password: "Assessor@123" },
  { role: "Client Viewer", email: "viewer@cyberassess.com", password: "Viewer@123" },
];

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#0A0A0A] text-white">
      {/* Left: branding panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-zinc-800 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              "url(https://static.prod-images.emergentagent.com/jobs/e0dbbd22-5ca8-4b31-8034-be4a8ec21296/images/3aa8b3224bc6db9074e3238a252b6ff85b98b341a7428762c18efe60d802a426.png)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md border border-zinc-700 bg-[#121212] flex items-center justify-center">
            <Shield className="w-5 h-5" aria-hidden />
          </div>
          <div>
            <div className="text-sm font-medium tracking-tight">CyberAssess</div>
            <div className="label-uppercase">NIST CSF 2.0 Maturity Platform</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="label-uppercase text-zinc-500 mb-3">Cybersecurity Maturity, Quantified.</div>
          <h1 className="text-4xl tracking-tight font-medium leading-tight">
            Run rigorous, evidence-driven assessments against the
            <span className="text-white"> NIST Cybersecurity Framework 2.0</span>.
          </h1>
          <p className="text-sm text-zinc-400 mt-5 leading-relaxed">
            Govern. Identify. Protect. Detect. Respond. Recover. — Map every subcategory to maturity, evidence, and remediation.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-8 text-xs">
            {[
              ["106", "Subcategories scored"],
              ["6", "CSF 2.0 functions"],
              ["4", "Maturity tiers"],
              ["0–5", "Evidence-capped scoring"],
            ].map(([v, l]) => (
              <div key={l} className="border border-zinc-800 bg-[#121212]/80 backdrop-blur-sm rounded-md p-3">
                <div className="text-2xl font-medium tracking-tight mono">{v}</div>
                <div className="label-uppercase mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-[11px] text-zinc-600">
          © {new Date().getFullYear()} CyberAssess. Multi-tenant GRC platform.
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-md border border-zinc-700 bg-[#121212] flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div className="text-sm font-medium tracking-tight">CyberAssess</div>
          </div>
          <div className="label-uppercase mb-2">Sign in</div>
          <h2 className="text-3xl tracking-tight font-medium mb-2">Access your assessments</h2>
          <p className="text-sm text-zinc-500 mb-8">Enter your credentials below.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label className="label-uppercase">Email</Label>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email-input"
                placeholder="you@company.com"
                className="mt-1.5 h-11 bg-[#121212] border-zinc-800 focus-visible:ring-zinc-400 focus-visible:border-zinc-400"
              />
            </div>
            <div>
              <Label className="label-uppercase">Password</Label>
              <Input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password-input"
                placeholder="••••••••"
                className="mt-1.5 h-11 bg-[#121212] border-zinc-800 focus-visible:ring-zinc-400 focus-visible:border-zinc-400"
              />
            </div>
            {err && (
              <div className="text-xs text-red-400 border border-red-500/20 bg-red-500/5 rounded-md px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}
            <Button
              type="submit" disabled={busy} data-testid="login-submit-button"
              className="w-full h-11 bg-white text-black hover:bg-zinc-200 font-medium"
            >
              <Lock className="w-4 h-4 mr-2" /> {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-10 border border-zinc-800 rounded-md p-4 bg-[#121212]" data-testid="demo-accounts">
            <div className="label-uppercase mb-3">Demo accounts</div>
            <div className="space-y-2">
              {DEMO_ACCOUNTS.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => { setEmail(d.email); setPassword(d.password); }}
                  data-testid={`demo-account-${d.role.replace(/\s+/g, "-").toLowerCase()}`}
                  className="w-full text-left flex items-center justify-between gap-3 p-2.5 rounded-md border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/50 transition-colors"
                >
                  <div>
                    <div className="text-xs text-white font-medium">{d.role}</div>
                    <div className="text-[11px] text-zinc-500 mono">{d.email}</div>
                  </div>
                  <div className="label-uppercase text-zinc-500">Use →</div>
                </button>
              ))}
            </div>
            <div className="text-[10px] text-zinc-600 mt-3 leading-relaxed">
              Demo accounts will prompt for a password change on first sign-in.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
